import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Modal, Select, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import operationService, {
  Operation,
  OperationType,
  SkillLevel,
} from '@/services/operation.service';

export type QuickCreateOperationModalProps = {
  open: boolean;
  onCancel: () => void;
  onCreated: (operation: Operation) => void;
  /** Prefill operation name (e.g. from Select search). */
  presetName?: string;
};

function getCompanyId(): string {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.company_id || '';
  } catch {
    return '';
  }
}

function suggestCode(name: string): string {
  const slug = String(name || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 16);
  return slug ? `OP-${slug}` : '';
}

/**
 * Lean Operations Master create for estimation / BOQ Add Operation.
 * Mirrors QuickCreateItemModal: code + name + type required; times/cost optional with defaults.
 */
export const QuickCreateOperationModal: React.FC<QuickCreateOperationModalProps> = ({
  open,
  onCancel,
  onCreated,
  presetName,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open) return;
    const name = String(presetName || '').trim().slice(0, 255);
    form.resetFields();
    form.setFieldsValue({
      operation_name: name,
      operation_code: suggestCode(name),
      operation_type: OperationType.MACHINING,
      skill_level_required: SkillLevel.INTERMEDIATE,
      standard_setup_time_hours: 0,
      standard_run_time_per_unit_hours: 0,
      per_hour_cost: 0,
      description: undefined,
    });
  }, [open, presetName, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const company_id = getCompanyId();
      const created = await operationService.create({
        company_id,
        operation_code: String(values.operation_code).trim(),
        operation_name: String(values.operation_name).trim(),
        operation_type: values.operation_type,
        skill_level_required: values.skill_level_required || SkillLevel.INTERMEDIATE,
        standard_setup_time_hours: Number(values.standard_setup_time_hours ?? 0),
        standard_run_time_per_unit_hours: Number(values.standard_run_time_per_unit_hours ?? 0),
        standard_teardown_time_hours: 0,
        per_hour_cost: Number(values.per_hour_cost ?? 0),
        description: values.description ? String(values.description).trim() : undefined,
        is_active: true,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['operations'] }),
        queryClient.invalidateQueries({ queryKey: ['manufacturing-operations-master'] }),
      ]);
      message.success('Operation created');
      onCreated(created);
      onCancel();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create operation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Create operation"
      open={open}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      confirmLoading={saving}
      okText="Create"
      destroyOnClose
      width={560}
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="operation_code"
          label="Operation code"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Input placeholder="e.g. OP-CNC-MILL" />
        </Form.Item>
        <Form.Item
          name="operation_name"
          label="Operation name"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Input placeholder="e.g. CNC Milling" />
        </Form.Item>
        <Form.Item
          name="operation_type"
          label="Operation type"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Select
            options={Object.values(OperationType).map((type) => ({ value: type, label: type }))}
          />
        </Form.Item>
        <Form.Item name="skill_level_required" label="Skill level" rules={[{ required: true }]}>
          <Select
            options={Object.values(SkillLevel).map((level) => ({ value: level, label: level }))}
          />
        </Form.Item>
        <Form.Item name="standard_setup_time_hours" label="Setup time (hours)">
          <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="standard_run_time_per_unit_hours" label="Run time per unit (hours)">
          <InputNumber min={0} step={0.01} precision={4} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="per_hour_cost" label="Cost per hour">
          <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="Optional" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default QuickCreateOperationModal;
